import { Subject } from 'rxjs';
import {
  Input,
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewEncapsulation,
  OnDestroy,
  EventEmitter,
  Optional,
  SkipSelf,
  Output,
  ElementRef,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormControlDirective, NgControl, ValidatorFn } from '@angular/forms';
import mixinDisabled from '../../mixins/disabled';
import { PHOENIX_CONTEXTS, PhxContextService } from '../../services/phx-context-service';
import { Check, Cross, MagnifyingGlass } from './../icon/icon-map';
import { PhxInputStyle } from './phx-input.component';

const baseInputMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

@Component({
  selector: 'phx-search-input',
  templateUrl: './phx-search-input.component.html',
  styleUrls: ['./phx-search-input.component.scss'],
  inputs: ['disabled'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-search-input',
    '[class.phx-filter]': 'phxContext === PHOENIX_CONTEXTS.FILTER',
    '[attr.phx-non-default-selected]': 'isValueSelected'
  },
})
export class PhxSearchInput extends baseInputMixin implements ControlValueAccessor, OnDestroy {
  @Input() public phxInputStyle: PhxInputStyle = 'default';
  @Input() public phxPlaceholder?: string;
  @Input() public phxAutoFocus?: boolean;

  @Output() public phxOnClearClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() public phxFocus: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
  @Output() public phxBlur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

  public readonly crossIcon: string = Cross;
  public readonly checkIcon: string = Check;
  public searchValue: FormControl;
  public formError: boolean = false;
  public MagnifyingGlassIcon: string = MagnifyingGlass;
  public phxContext: PHOENIX_CONTEXTS;
  public readonly PHOENIX_CONTEXTS = PHOENIX_CONTEXTS;
  private onChange = (val) => {};
  private onTouched = () => {};
  private destroy$ = new Subject<void>();
  private control: NgControl | FormControlDirective;
  private el: HTMLElement;

  constructor(
    elRef: ElementRef,
    private cdr: ChangeDetectorRef,
    @Optional() private reactiveControl: FormControlDirective,
    @Optional() private templateControl: NgControl,
    @Optional() @SkipSelf() contextService: PhxContextService
  ) {
    super();
    this.el = elRef.nativeElement;
    this.control = this.reactiveControl || this.templateControl;
    this.searchValue = new FormControl(this.control.value, this.control.validator);
    this.control.valueAccessor = this;
    this.phxContext = contextService?.getType();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    if (this.phxAutoFocus) {
      this.focus();
    }
  }

  updateModel(): void {
    this.onChange(this.searchValue.value);
  }

  writeValue(value: string): void {
    this.searchValue.setValue(value);
    this.searchValue.setValidators(this.getFormValidator());
    this.searchValue.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  getFormValidator(): ValidatorFn {
    return this.reactiveControl ? this.reactiveControl.form.validator : this.templateControl.validator;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onClear(): void {
    if (this.disabled) return;
    this.searchValue.setValue(null);
    this.updateModel();
    this.phxOnClearClick.emit();
  }

  public get isValueSelected(): boolean {
    return !!this.searchValue.value;
  }

  focus(): void {
    setTimeout(() => this.focusInput());
  }

  private focusInput(): void {
    const inputElement = this.el.querySelector('input');
    if (inputElement) {
      inputElement.focus();
    }
  }
}
